import { useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Transition } from "react-transition-group";

import { FILTERING_TRANSITION_DURATION } from "../../../config/constants";
import { twMerge } from "../../helpers/twMerge";
import { getIsNotUserFiltered, getIsTransitionApplied } from "../../selectors";

/**
 * BaseTransitionWrapper - A reusable wrapper component that provides standardized filtering transition effects
 *
 * Features:
 * - Opacity transition based on global transition state
 * - Configurable transition duration and appearance settings
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap with transitions
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {number} [props.transitionDuration] - Transition duration in ms
 * @param {boolean} [props.appear] - Whether to show appear animation
 * @param {boolean} [props.unmountOnExit] - Whether to unmount component on exit
 */
export const BaseTransitionWrapper = ({
	children,
	className = "",
	transitionDuration = FILTERING_TRANSITION_DURATION,
	appear,
	unmountOnExit = true,
}) => {
	const nodeRef = useRef(null);
	const isNotUserFiltered = useSelector(getIsNotUserFiltered);
	const isTransitionApplied = useSelector(getIsTransitionApplied);

	// Determine appear behavior - if not explicitly set, use inverse of isNotUserFiltered
	const shouldAppear = appear !== undefined ? appear : !isNotUserFiltered;

	return (
		<Transition
			nodeRef={nodeRef}
			in
			appear={shouldAppear}
			timeout={transitionDuration}
			unmountOnExit={unmountOnExit}
		>
			<div
				className={twMerge(
					className,
					"tw:transition-all",
					isTransitionApplied ? "tw:opacity-50" : "",
				)}
			>
				{children}
			</div>
		</Transition>
	);
};

BaseTransitionWrapper.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	transitionDuration: PropTypes.number,
	appear: PropTypes.bool,
	unmountOnExit: PropTypes.bool,
};
